/**
 * TextEditOverlay — the edit button a superadmin sees on every page.
 *
 * Nothing here renders for anyone else, and the save endpoints refuse a
 * non-superadmin anyway, so the button is a convenience rather than the
 * security boundary.
 *
 * With edit mode on:
 *   - every <T> on the page carries its locale key, so a click lands on
 *     exactly the right piece of text with no guessing;
 *   - text that lives in an attribute (placeholder, tooltip, alt text) is
 *     matched by its current wording and made clickable too;
 *   - anything that still cannot be clicked — a browser dialog, a label on a
 *     dropdown option — is reachable through the list of all text on the page.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useLanguageStore } from '@/stores/languageStore';
import { useUiTextStore, getPageKeys, clearPageKeys } from '@/stores/uiTextStore';
import { resolveText, useT } from '@/lib/i18n';
import { T } from '@/components/T';
import { stripFormatting } from '@/lib/richText';
import TextEditPanel from './TextEditPanel';

/** Attributes whose text comes from the locales and is worth editing. */
const TEXT_ATTRIBUTES = ['placeholder', 'title', 'alt', 'aria-label'] as const;

export default function TextEditOverlay() {
  const t = useT();
  const role = useAuthStore((s) => s.user?.role);
  const language = useLanguageStore((s) => s.language);
  const overrides = useUiTextStore((s) => s.overrides);
  const editMode = useUiTextStore((s) => s.editMode);
  const setEditMode = useUiTextStore((s) => s.setEditMode);
  const editingKey = useUiTextStore((s) => s.editingKey);
  const openEditor = useUiTextStore((s) => s.openEditor);
  const closeEditor = useUiTextStore((s) => s.closeEditor);
  const location = useLocation();
  const [browsing, setBrowsing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const isSuperadmin = role === 'superadmin';
  const panelOpen = Boolean(editingKey) || browsing;

  // A new page means a new set of text on screen.
  useEffect(() => {
    clearPageKeys();
  }, [location.pathname]);

  // Outline the editable text while edit mode is on.
  useEffect(() => {
    if (!isSuperadmin) return;
    document.body.classList.toggle('artext-editing', editMode);
    return () => document.body.classList.remove('artext-editing');
  }, [editMode, isSuperadmin]);

  // A click on marked text opens the editor instead of following the link or
  // pressing the button underneath it.
  useEffect(() => {
    if (!isSuperadmin || !editMode) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest) return;
      if (target.closest('.artext-panel') || target.closest('.artext-controls')) return;

      const marked = target.closest('[data-artext-key]') as HTMLElement | null;
      if (!marked) return;

      e.preventDefault();
      e.stopPropagation();
      setBrowsing(false);
      openEditor(marked.getAttribute('data-artext-key') as string);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isSuperadmin, editMode, openEditor]);

  // Mark elements whose placeholder / tooltip / alt text is one of the keys
  // this page used. React re-renders drop the marks, so they are re-applied
  // whenever the page changes.
  useEffect(() => {
    if (!isSuperadmin || !editMode) return;

    let frame = 0;

    const markAttributes = () => {
      frame = 0;
      const byText = new Map<string, string>();
      for (const key of getPageKeys()) {
        const { value, fromOverride } = resolveText(key, language, overrides);
        const plain = fromOverride ? stripFormatting(value) : value;
        // A wording used by two keys is ambiguous; the page list is the
        // honest way to reach those, so they are left unmarked.
        byText.set(plain, byText.has(plain) ? '' : key);
      }

      const selector = TEXT_ATTRIBUTES.map((a) => `[${a}]`).join(',');
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        if (el.closest('.artext-panel') || el.closest('.artext-controls')) return;
        if (el.hasAttribute('data-artext-key')) return;
        for (const attr of TEXT_ATTRIBUTES) {
          const value = el.getAttribute(attr);
          if (!value) continue;
          const key = byText.get(value.trim());
          if (key) {
            el.setAttribute('data-artext-key', key);
            el.setAttribute('data-artext-attr', attr);
            return;
          }
        }
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(markAttributes);
    };

    schedule();
    // childList only: marking sets attributes, and watching attributes here
    // would make the observer retrigger itself.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      document.querySelectorAll('[data-artext-attr]').forEach((el) => {
        el.removeAttribute('data-artext-key');
        el.removeAttribute('data-artext-attr');
      });
    };
  }, [isSuperadmin, editMode, language, overrides]);

  if (!isSuperadmin) return null;

  const closePanel = () => {
    setBrowsing(false);
    closeEditor();
  };

  return (
    <>
      <div className="artext-controls" ref={overlayRef}>
        {editMode && (
          <button
            type="button"
            className="artext-control-btn"
            onClick={() => { closeEditor(); setBrowsing(true); }}
            title={t('textEditor.pageList')}
          >
            <i className="fas fa-list" /> <span><T k="textEditor.pageList" /></span>
          </button>
        )}
        <button
          type="button"
          className={`artext-control-btn artext-toggle${editMode ? ' artext-toggle-on' : ''}`}
          onClick={() => { setEditMode(!editMode); setBrowsing(false); }}
          title={editMode ? t('textEditor.exit') : t('textEditor.enter')}
        >
          <i className={`fas ${editMode ? 'fa-check' : 'fa-pen-to-square'}`} />
          <span>{editMode ? t('textEditor.exit') : t('textEditor.enter')}</span>
        </button>
      </div>

      {editMode && !panelOpen && (
        <div className="artext-banner" role="status"><T k="textEditor.banner" /></div>
      )}

      {panelOpen && <TextEditPanel textKey={editingKey} onClose={closePanel} />}
    </>
  );
}
